import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send, TriangleAlert, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useSiteContent } from "../hooks/useSiteContent";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const serviceOptions = [
  "Explosives & Narcotics Sweeps",
  "Knife & Weapon Detection",
  "Request a Dog at Your Event",
  "School & Venue Inspections",
  "Corporate & Event Protection",
  "Dogs For Sale Enquiry",
  "Rehoming - I can't house my dog anymore",
  "Not sure yet - advise me",
];

export default function Contact() {
  const content = useSiteContent();
  const contact = content?.contact || {};
  const pg = content?.pages?.contact || {};
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    event_date: "",
    event_time: "",
    location: "",
    urgent: false,
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const s = searchParams.get("service");
    const dog = searchParams.get("dog");
    if (!s && !dog) return;
    setForm((f) => ({
      ...f,
      service: s && serviceOptions.includes(s) ? s : f.service,
      message: dog ? `Hi, I'm interested in ${dog}. Can you send me more details?` : f.message,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Message sent", { description: "We'll be in touch shortly - usually within the hour." });
      setForm({ name: "", email: "", phone: "", service: "", event_date: "", event_time: "", location: "", urgent: false, message: "" });
    } catch (e2) {
      const msg = formatApiError(e2);
      setError(msg);
      toast.error("Could not send your message", { description: msg });
    } finally {
      setSending(false);
    }
  };

  const cards = [
    { icon: Phone, label: "24/7 Rapid Response", value: contact.phone || "", href: `tel:${contact.phone || ""}` },
    { icon: Mail, label: "Email", value: contact.email || "", href: `mailto:${contact.email || ""}` },
    { icon: MapPin, label: "Coverage", value: contact.coverage || "" },
    { icon: Clock, label: "Availability", value: contact.hours || "" },
  ].filter((c) => c.value);

  return (
    <main className="bg-brand-obsidian pt-28 sm:pt-32" data-testid="contact-page">
      <section className="px-4 sm:px-6 pb-24">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> {pg.eyebrow || "Contact Us"}</Eyebrow>
            <h1 className="mt-4 font-display font-extrabold uppercase text-3xl sm:text-5xl text-white tracking-tight">
              {pg.title || "Deploy"} <span className="text-brand-crimson">{pg.title_accent || "Quest K9 Teams"}</span>
            </h1>
            <p className="mt-4 max-w-2xl text-slate-400 leading-relaxed">
              {pg.intro || "Tell us about your venue, dates and concerns. We reply fast - and urgent requests jump the queue."}
            </p>
          </Reveal>

          <div className="mt-12 grid lg:grid-cols-[1.4fr_1fr] gap-10 pb-4">
            <Reveal>
              <form onSubmit={submit} className="rounded-lg border border-brand-line bg-brand-surface p-6 sm:p-8 space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="c-name" className="text-white/80 text-sm">{pg.f_name || "Full Name *"}</Label>
                    <Input id="c-name" required data-testid="contact-input-name" value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="Jane Smith" className="bg-brand-obsidian border-brand-line" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c-email" className="text-white/80 text-sm">{pg.f_email || "Email *"}</Label>
                    <Input id="c-email" required type="email" data-testid="contact-input-email" value={form.email} onChange={(e) => set("email")(e.target.value)} placeholder="jane@company.co.uk" className="bg-brand-obsidian border-brand-line" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="c-phone" className="text-white/80 text-sm">{pg.f_phone || "Phone"}</Label>
                    <Input id="c-phone" type="tel" data-testid="contact-input-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} placeholder="07700 900000" className="bg-brand-obsidian border-brand-line" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c-date" className="text-white/80 text-sm">{pg.f_date || "Date Needed"}</Label>
                    <Input id="c-date" type="date" data-testid="contact-input-date" value={form.event_date} onChange={(e) => set("event_date")(e.target.value)} className="bg-brand-obsidian border-brand-line" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="c-time" className="text-white/80 text-sm">{pg.f_time || "Time Needed"}</Label>
                    <Input id="c-time" type="time" data-testid="contact-input-time" value={form.event_time} onChange={(e) => set("event_time")(e.target.value)} className="bg-brand-obsidian border-brand-line" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="c-location" className="text-white/80 text-sm">{pg.f_location || "Location"}</Label>
                    <Input id="c-location" data-testid="contact-input-location" value={form.location} onChange={(e) => set("location")(e.target.value)} placeholder="Town / venue" className="bg-brand-obsidian border-brand-line" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80 text-sm">{pg.f_service || "Service Required"}</Label>
                  <div className="relative">
                    <select
                      data-testid="contact-select-service"
                      value={form.service}
                      onChange={(e) => set("service")(e.target.value)}
                      className={`h-9 w-full appearance-none rounded-md border border-brand-line bg-brand-obsidian px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring ${form.service ? "text-white" : "text-white/50"}`}
                    >
                      <option value="">{pg.f_service_ph || "Choose a service"}</option>
                      {serviceOptions.map((s) => (
                        <option key={s} value={s} data-testid={`contact-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s}</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-message" className="text-white/80 text-sm">{pg.f_message || "Your Message *"}</Label>
                  <Textarea id="c-message" required data-testid="contact-input-message" value={form.message} onChange={(e) => set("message")(e.target.value)} rows={5} placeholder="Venue type, expected attendance, what you're concerned about..." className="bg-brand-obsidian border-brand-line" />
                </div>
                <div className="flex items-center justify-between rounded-md border border-brand-line bg-brand-obsidian px-4 py-3">
                  <span className="flex items-center gap-2 text-sm text-white/80">
                    <TriangleAlert className="h-4 w-4 text-brand-crimson" /> {pg.urgent_label || "This is urgent / same-day"}
                  </span>
                  <Switch checked={form.urgent} onCheckedChange={set("urgent")} data-testid="contact-switch-urgent" />
                </div>
                {error && (
                  <p className="text-sm text-red-400" data-testid="contact-form-error">{error}</p>
                )}
                <Button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="w-full bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow uppercase tracking-[0.2em] text-xs font-bold py-6 rounded-md transition-all hover:shadow-[0_0_32px_rgba(200,16,46,0.55)]"
                >
                  {sending ? pg.sending || "Sending..." : pg.submit || "Send Message"}
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </Reveal>

            <div className="space-y-4">
              {cards.map((c, i) => (
                <Reveal key={c.label} delay={0.1 + i * 0.07}>
                  <div className="flex items-start gap-4 rounded-lg border border-brand-line bg-brand-surface p-6 hover:border-brand-crimson/60 transition-colors" data-testid={`contact-card-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <div className="shrink-0 inline-flex items-center justify-center h-11 w-11 rounded-md bg-brand-crimson/15 border border-brand-crimson/30 text-brand-crimson">
                      <c.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">{c.label}</p>
                      {c.href ? (
                        <a href={c.href} className="mt-1 block text-white font-semibold hover:text-brand-crimson transition-colors">{c.value}</a>
                      ) : (
                        <p className="mt-1 text-white font-semibold">{c.value}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
